import {
  EndpointAddonRaw,
  EndpointAddonRawForm,
  EndpointAddonUpdateRawForm,
} from "@/domain/EndpointAddon";
import { API_CLIENT } from "../config";
import { getAPIConfigs } from "../helpers";

const apiUrlEndpointAddons = "admin/endpoint_addons";

export const getAdminEndpointAddons = async ({
  organizationId,
}: {
  organizationId: string;
}): Promise<EndpointAddonRaw[]> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const response = await API_CLIENT.get<{ items: EndpointAddonRaw[] }>(
    `${apiUrlEndpointAddons}`,
    {
      ...apiOrganizationIdParams,
    }
  );
  return response.data.items;
};

export const getAdminEndpointAddon = async ({
  organizationId,
  endpointAddonId,
}: {
  organizationId: string;
  endpointAddonId: EndpointAddonRaw["id"];
}): Promise<EndpointAddonRaw> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const response = await API_CLIENT.get<{ item: EndpointAddonRaw }>(
    `${apiUrlEndpointAddons}/${endpointAddonId}`,
    {
      ...apiOrganizationIdParams,
    }
  );
  return response.data.item;
};

export const postAdminCreateEndpointAddon = async ({
  organizationId,
  endpointAddonRawForm,
}: {
  organizationId: string;
  endpointAddonRawForm: EndpointAddonRawForm;
}): Promise<EndpointAddonRaw> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const response = await API_CLIENT.post<{ item: EndpointAddonRaw }>(
    `${apiUrlEndpointAddons}`,
    endpointAddonRawForm,
    {
      ...apiOrganizationIdParams,
    }
  );
  return response.data.item;
};

export const postAdminEditEndpointAddon = async ({
  organizationId,
  endpointAddonId,
  endpointAddonUpdateRawForm,
}: {
  organizationId: string;
  endpointAddonId: EndpointAddonRaw["id"];
  endpointAddonUpdateRawForm: EndpointAddonUpdateRawForm;
}): Promise<EndpointAddonRaw> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const response = await API_CLIENT.post<{ item: EndpointAddonRaw }>(
    `${apiUrlEndpointAddons}/${endpointAddonId}`,
    endpointAddonUpdateRawForm,
    {
      ...apiOrganizationIdParams,
    }
  );
  return response.data.item;
};

export const postAdminDisableEndpointAddon = async ({
  organizationId,
  endpointAddonId,
}: {
  organizationId: string;
  endpointAddonId: EndpointAddonRaw["id"];
}): Promise<EndpointAddonRaw> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const response = await API_CLIENT.post<{ item: EndpointAddonRaw }>(
    `${apiUrlEndpointAddons}/${endpointAddonId}/delete`,
    undefined,
    {
      ...apiOrganizationIdParams,
    }
  );
  return response.data.item;
};

export const postAdminEnableEndpointAddon = async ({
  organizationId,
  endpointAddonId,
}: {
  organizationId: string;
  endpointAddonId: EndpointAddonRaw["id"];
}): Promise<EndpointAddonRaw> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const response = await API_CLIENT.post<{ item: EndpointAddonRaw }>(
    `${apiUrlEndpointAddons}/${endpointAddonId}/enable`,
    undefined,
    {
      ...apiOrganizationIdParams,
    }
  );
  return response.data.item;
};

export const postAdminRemoveEndpointAddon = async ({
  organizationId,
  endpointAddonId,
}: {
  organizationId: string;
  endpointAddonId: EndpointAddonRaw["id"];
}): Promise<void> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  await API_CLIENT.post(
    `${apiUrlEndpointAddons}/${endpointAddonId}/remove`,
    undefined,
    {
      ...apiOrganizationIdParams,
    }
  );
};

// Endpoint Addon の secret_env_vars に対応する Secret Manager 上の現在値を取得する。
// 値の閲覧は aa:developer 以上に制限される (バックエンド側で認可)。
// Connect マスタの共通シークレット取得 API と同じく name → value のマップを返す。
export const getAdminEndpointAddonSecrets = async ({
  organizationId,
  endpointAddonId,
}: {
  organizationId: string;
  endpointAddonId: EndpointAddonRaw["id"];
}): Promise<{ [k: string]: string }> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const response = await API_CLIENT.get<{ item: { [k: string]: string } }>(
    `${apiUrlEndpointAddons}/${endpointAddonId}/secrets`,
    {
      ...apiOrganizationIdParams,
    }
  );
  return response.data.item;
};

// Endpoint Addon の secret_env_vars を送信したマップで全置換する (Connect マスタの
// 共通シークレット変更 API と同じ全置換方式)。書き込みは aa:developer 以上に制限される。
export const postAdminEndpointAddonSecrets = async ({
  organizationId,
  endpointAddonId,
  item,
}: {
  organizationId: string;
  endpointAddonId: EndpointAddonRaw["id"];
  item: { [k: string]: string };
}): Promise<EndpointAddonRaw> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const response = await API_CLIENT.post<{ item: EndpointAddonRaw }>(
    `${apiUrlEndpointAddons}/${endpointAddonId}/secrets`,
    { item },
    {
      ...apiOrganizationIdParams,
    }
  );
  return response.data.item;
};

export const postAdminRedeployEndpointAddon = async ({
  organizationId,
  endpointAddonId,
}: {
  organizationId: string;
  endpointAddonId: EndpointAddonRaw["id"];
}): Promise<EndpointAddonRaw> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const response = await API_CLIENT.post<{ item: EndpointAddonRaw }>(
    `${apiUrlEndpointAddons}/${endpointAddonId}/redeploy`,
    undefined,
    {
      ...apiOrganizationIdParams,
    }
  );
  return response.data.item;
};
